/**
 * useNextHandler is a custom hook that provides access to the NextHandlerContext.
 *
 * This context is designed to manage navigation-related state and actions
 * across multiple components or steps, such as "Next" and "Back" handlers.
 * It provides the ability to:
 *
 * - Set and run a "Next" action handler (setHandler, runHandler).
 * - Set and run a "Back" action handler (setBackHandler, onBack).
 * - Control the visibility of "Next" and "Back" buttons (setShowNext, setShowBack).
 * - Enable or disable navigation (setDisabled).
 *
 * Use this hook inside components that are wrapped with <NextHandlerProvider>
 * to interact with and control step navigation behavior in a centralized way.
 *
 * If the hook is used outside the provider, it will return default no-op functions.
 */

import {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from 'react';

type NextHandlerContextType = {
  disabled?: boolean;
  backDisabled?: boolean;
  showBack?: boolean;
  showNext?: boolean;
  onBack?: () => void;
  setHandler: (fn: () => void) => void;
  setBackHandler: (fn: () => void) => void;
  setShowNext: (value: boolean) => void;
  setShowBack: (value: boolean) => void;
  setDisabled: (value: boolean) => void;
  setBackDisabled: (value: boolean) => void;
  runHandler: () => void;
};

const defaultContext = {
  setHandler: () => {},
  setBackHandler: () => {},
  setShowNext: () => {},
  setShowBack: () => {},
  setDisabled: () => {},
  setBackDisabled: () => {},
  runHandler: () => {},
};

const NextHandlerContext = createContext<NextHandlerContextType>(defaultContext);

export const NextHandlerProvider = ({ children }: { children: ReactNode }) => {
  const handlerRef = useRef<() => void>(() => {});
  const backHandlerRef = useRef<() => void>(() => {});
  const [disabled, setDisabled] = useState(false);
  const [backDisabled, setBackDisabled] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const [showNext, setShowNext] = useState(false);

  const setHandler = (fn: () => void) => {
    handlerRef.current = fn;
  };

  const runHandler = () => {
    handlerRef.current();
  };

  const setBackHandler = (fn: () => void) => {
    backHandlerRef.current = fn;
  };

  return (
    <NextHandlerContext.Provider
      value={{
        setHandler,
        runHandler,
        disabled,
        backDisabled,
        setBackDisabled,
        setDisabled,
        showBack,
        setShowBack,
        showNext,
        setShowNext,
        setBackHandler,
        onBack: () => backHandlerRef.current(),
      }}
    >
      {children}
    </NextHandlerContext.Provider>
  );
};

export const useNextHandler = () => {
  const ctx = useContext(NextHandlerContext);

  return ctx;
};
